/** Helper to extract name and institution name */
import { AccountModel } from 'src/types/account.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import { DebtModel } from 'src/types/debt.model';
import ManualAccountService from 'src/services/manualAccount.service';
import AccountService from 'src/services/account.service';
import FormatService from 'src/services/format.service';
import { AccountSyncResult, AccountSyncStatusModel } from 'src/types/account-sync-status.model';
import { uniq } from 'lodash';

export function getAccountNames(
    anyAccount:
        | {
              account: AccountModel;
          }
        | {
              investmentHolding: InvestmentHoldingModel;
          }
        | {
              debt: DebtModel;
          }
): {
    name: string;
    institutionName: string;
} {
    if ('account' in anyAccount)
        return {
            name: AccountService.getDisplayName(anyAccount.account),
            institutionName: anyAccount.account.institution.name
        };

    if ('investmentHolding' in anyAccount)
        return {
            name: anyAccount.investmentHolding.name,
            institutionName:
                ManualAccountService.addAccountForms[
                    ManualAccountService.getManualAccountType({ investmentHolding: anyAccount.investmentHolding })
                ].label
        };

    return {
        name: anyAccount.debt.name,
        institutionName:
            ManualAccountService.addAccountForms[ManualAccountService.getManualAccountType({ debt: anyAccount.debt })]
                .label
    };
}

export function sortAccountsAlphabetically(accounts: AccountModel[]): AccountModel[] {
    return accounts.sort((a, b) => {
        const { name: aName, institutionName: aInstitutionName } = getAccountNames({ account: a });
        const { name: bName, institutionName: bInstitutionName } = getAccountNames({ account: b });
        return aInstitutionName === bInstitutionName
            ? aName.localeCompare(bName)
            : aInstitutionName.localeCompare(bInstitutionName);
    });
}

export function getAccountTagName(account: AccountModel): string {
    return FormatService.formatTag(FormatService.replaceDigits(AccountService.getDisplayName(account)));
}

export function getSyncingInstitutions(statuses: AccountSyncStatusModel[], accounts: AccountModel[]): string[] {
    const syncingAccountIds: string[] = (
        statuses ? statuses.filter((status) => status.syncResult === AccountSyncResult.SYNCING) : []
    ).map((status) => status.accountId);

    if (syncingAccountIds.length === 0) return [];

    const syncingInstitutionIds: string[] = accounts
        ? accounts
              .filter((account) => syncingAccountIds.includes(account.accountId))
              .map((account) => account.institution.institutionId)
        : [];

    return uniq(syncingInstitutionIds);
}
